/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2023
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'./FeatureLoaderAPI',
	'./FeatureProfileHelper',
	'underscore'
], function (APIFactory, ClassFactory, FeatureLoaderAPI, FeatureProfileHelper, _) {
	'use strict';


	/**
	 *  Create the feature dependency map that will be passed to a given feature in the constructor
	 *  We define the getter function where we check if the feature can access the feature at init time or not.
	 *
	 * @param {String} featureName  - Feature name
	 * @param {Object[]} dependencyList  List of dependencies that the feature requires. The list contains a list of objects that describe a feature
	 * @param {String} dependency.name  dependency name
	 * @param {boolean} dependency.init  True if the dependency is an init time dependency
	 * @param {FeatureLoader} dependency.loader  The feature loader used to access the feature
	 * @param {Object} [loadingState] the loading state of a given feature. Used to check we are in the init phase or not
	 * @param {Bboolean} [loadingState.loaded] Indicates if the feature is done loading or not
	 */
	const createFeatureDependencies = (featureName, dependencyList, loadingState) => {
		const map = {};
		if (dependencyList){
			for (let name  in dependencyList) {
				const dep = dependencyList[name];
				Object.defineProperty(map, name, {
					get() {
						if (!dep.init && !loadingState.loaded){
							throw new Error('The required feature "' + dep.name +'" cannot be accessed at init time by the feature"' + featureName + '". The feature "'+ dep.name +'" must be added to the initDependencies of feature "'+ +featureName + '" if it is needed at init time.' );
						}
						return dep.loader.getFeature(dep.name);
					}
				});
			}
		}
		return map;
	};

	const MODULE_LOADING_ERROR = {};

	class FeatureLoader {
		/**
		 *
		 * @param {Object} options
		 * @param {Object} options.dashboardAPI
		 * @param {Object} options.featureParams - parameters to be passed to the feautre when it is created
		 * @param {String} options.collectionName - collection name that contains the features to be loaderd
		 * @param {Object[]} options.inlineFeatures - array of inline feature to be created
		 * @param {Object} options.lifeCycleManager  - lifecyclemanager
		 */
		constructor(options) {
			this.externalFeatureLoader = options.externalFeatures;
			this.featureNamePrefix = options.featureNamePrefix;
			this.featureParams = options.featureParams;
			this.features = {};
			this.lifeCycleManager = options.lifeCycleManager;
			this.loadingOrder = [];
			this.errors = {};
			this.logger = options.logger || console;
			this.types = options.types || [];
			this.parentAPI = options.parentAPI;

			// todo - remove the featureSet -- it is not needed
			this.featureSet = options.featureSet;

			//helper for delay loading features according to profile
			let enableProfileFeatureLoading = options.enableProfileFeatureLoading;
			if( enableProfileFeatureLoading ) {
				this._profileHelper = new FeatureProfileHelper(options.profileList);
				this._enableProfileFeatureLoading = true;
			} else {
				this._enableProfileFeatureLoading = false;
			}

			this.feature2ProfileMap = {};

		}

		getAPI() {
			if (!this._api) {
				this._api =  APIFactory.createAPI(this, [FeatureLoaderAPI]);
			}
			return this._api;
		}

		getCurrentProfile() {
			return this._profileHelper && this._profileHelper.getCurrentProfile();
		}

		updateCurrentProfile( params ) {
			if( !this._profileHelper) {
				return Promise.resolve(true);
			}
			const toRemove = params.remove;
			const toAdd = params.add;
			this._profileHelper.removeFromCurrentProfile(toRemove);
			const addedToCurrent = this._profileHelper.addToCurrentProfile(toAdd);
			if( !addedToCurrent ) {
				return Promise.resolve(true);
			}

			let pendingFeatures = this._profileHelper.getParkedFeatureList();
			let featuresToLoad  = [];
			for(let i=0; i<pendingFeatures.length; i++) {
				const toLoadItem = pendingFeatures[i];
				if( !this.features[toLoadItem.name] ) {
					featuresToLoad.push( toLoadItem );
				}
			}
			this._profileHelper.setParkedFeatureList(featuresToLoad);
			return this.loadFeaturesFromArray(featuresToLoad, [], true);
		}


		_getFeatureListThatInitDependsOn(featureName){
			const list = [];
			for (let name in this.features){
				if (this._getInitDependencies(name).indexOf(featureName) !== -1) {
					list.push(name);
				}
			}
			return list;
		}


		_getFeatureModule(feature){
			return (feature.module.default && _.isFunction(feature.module.default)) ? feature.module.default: feature.module;
		}


		_createFeature(feature){
			feature.isCreated = true;
			const params = feature.params;
			const module = this._getFeatureModule(feature);
			feature.instance = new module(params);
			return feature.instance;
		}

		/**
		 * Get the feature instance. This function will create the feature if it is hasn't been created.
		 * We will also create any feature that has init depenency on this feature.. These dependent features might want to register themselves with this feature
		 * This is typically when we have a "provider" type feature with the purpose of only registering itself in some other feature
		 * These features are not typically accessed directly, so they won't get created if we don't do this trick here
		 *
		 *
		 * @param {String} name
		 * @param {Object} options
		 * @param {Boolean} options.create
		 * @param {Boolean} doNotCreate
		 */
		_getFeatureInstance(name, options = { create:true }) {
			let instance;
			if (this.features[name]){
				if (options.create && !this.features[name].instance && ! this._getError(name)){
					try {
						//delayed creation of the feature
						const instance = this._createFeature(this.features[name]);
						this._postFeatureInitialize(this.features[name]);
						this.setParentChildRelation(name, instance);

						// Create any feature that has init depenency on this feature. See comments above
						// TODO - fix this function to also find features that depend on thi given feature when using a feature modifier/type
						// example : 3 feature: A, B , C
						//	A depends on C
						//  B depends on C.modifier
						// When C is created, we want to create A and B .. but the following function only finds A
						this._getFeatureListThatInitDependsOn(name).forEach(depName => {
							if (!this.features[depName].isCreated) {
								this._getFeatureInstance(depName);
							}
						});

					} catch (e) {
						this._setError(name, 'Error while creating the feature:' + name, e, this.features);
					}
				}
				instance = this.features[name].instance;
			}
			return instance;
		}

		_getFeatureTags(name) {
			return this.features[name] && this.features[name].spec && this.features[name].spec.tags || [];
		}
		_getOptions(name, features = this.features) {
			return features[name] && features[name].spec && features[name].spec.options;
		}


		_getInitDependencies(name, features = this.features) {
			const initDep = features[name] && features[name].spec && features[name].spec.dependencies || [];
			return Array.isArray(initDep) ? initDep : Object.keys(initDep);
		}

		_getRuntimeDependencies(name, features = this.features) {
			const runtimeDep = features[name] && features[name].spec && features[name].spec.runtimeDependencies || [];
			return Array.isArray(runtimeDep) ? runtimeDep : Object.keys(runtimeDep);
		}

		_getDependencies(name, features = this.features) {
			return this._getInitDependencies( name, features).concat(this._getRuntimeDependencies( name, features));
		}

		// Return the feature alias name if one is defined in the dependency
		_getDependenciesPreferredName(depName, name, features = this.features) {
			let preferredName = depName;
			// check init depdendencies
			const initDep = features[name] && features[name].spec && features[name].spec.dependencies;
			const runtimeDep = features[name] && features[name].spec && features[name].spec.runtimeDependencies;
			if (initDep && !Array.isArray(initDep) && initDep[depName]){
				preferredName = initDep[depName];
			}else if (runtimeDep && !Array.isArray(runtimeDep) && runtimeDep[depName]){
				preferredName = runtimeDep[depName];
			}
			return preferredName;
		}

		_getError(name, features = this.features) {
			return features[name] && features[name].error;
		}


		setParentChildRelation(name, instance){
			if(this.parentAPI){
				APIFactory.setParentChildRelation(this.parentAPI, instance, {
					propagationInfo: {
						namespace: name.toLowerCase(),
						info: {
							featureName: name
						},
						callStack:{
							name:'getFeature',
							params:[name]
						}
					}
				});
			}
		}
		/**
		 * A function to manually register a service. Could be used by the dashboard framework to register low level features.
		 * @param {String} name
		 * @param {Object} instance
		 * @param {Object} spec
		 */
		registerFeature(name, instance, spec, skipInitialization) {
			this.features[name] = {
				instance: instance,
				spec: spec || {},
				preparePromise: Promise.resolve(name)
			};

			if (this.featureNamePrefix){
				this.features[this.featureNamePrefix + '.' + name] = this.features[name] ;
			}

			let promise;
			if (skipInitialization){
				promise = Promise.resolve();
			}else {
				promise = this._initializeFeature(name);
			}
			return promise.then(() => {
				// Hook up API events after the feature is initialized
				this.setParentChildRelation(name, instance);
			});
		}

		/**
		 * Load the features from a collection json
		 *
		 * @param {Object[]} features
		 * @param {Object} [featureSpecs] Feature specfications provided as paramaters to features
		 *
		 */
		loadFeaturesFromArray(collectionItemsIn, featureSpecs, strictMode = false){

			let collectionItems;
			if( this._enableProfileFeatureLoading ) {
				let options =  {
					collectionItemsIn: collectionItemsIn,
					profileIn: this.getCurrentProfile(),
					strict: strictMode,
					excludedList: [],
					logger: this.logger
				};
				collectionItems = this._profileHelper.filterAndTrackFeaturesForProfile( options );
			} else {
				collectionItems = collectionItemsIn;
			}


			if (featureSpecs && Object.keys(featureSpecs).length > 0) {
				this.featureParams = this.featureParams ? this.featureParams : {};
				this.featureParams.featureSpecs = Object.assign({}, this.featureParams.featureSpecs, featureSpecs);
			}

			let featureAreLoaded = Promise.resolve();
			const features = {
			};
			const loadingState = {
				loaded: false,
				readyPromises: []
			};
			const classesToLoad = [];
			const featureIds = [];

			collectionItems.forEach((feature) => {
				const identifier = feature.name || feature.id;

				// If the feature has types make it matches the type associated with the feature loader
				let skipFeature = false;
				if (this.types.length > 0 && feature.types && feature.types.length > 0){
					let matchedType = this.types.find((type) => {
						return feature.types.indexOf(type) !== -1;
					} );

					skipFeature = !matchedType;
				}

				if (!skipFeature && (!this.featureSet || this.featureSet.indexOf(identifier) !== -1)) {
					features[identifier] = {
						spec: feature
					};
					if (MODULE_LOADING_ERROR[feature.class]){
						// We already failed while loading this class. don't try again. Rquire.j will not hang the second time
						this._setError(identifier, 'Failed to require feature', MODULE_LOADING_ERROR[feature.class].causedBy, features);
					} else {
						if (feature.class === undefined) {
							this._setError(identifier, 'feature with no defined class', '', features);
						} else if (FeatureLoader.classes[feature.class] || feature.class.prototype !== undefined) {
							this._registerModuleInfeatures(FeatureLoader.classes[feature.class] || feature.class, identifier, features);
						} else {
							classesToLoad.push(feature.class);
							featureIds.push(identifier);
						}
					}
				}
			});

			if (classesToLoad.length > 0) {
				featureAreLoaded = this._loadFeatureClasses(classesToLoad, featureIds, features);
			}

			// Create the features based on the dependencies list and initialize them
			return featureAreLoaded.then(() => {
				//Validate the dependencies
				this._validateDependencies(features);
				let name;
				for (name in features) {
					this._prepareFeature(name, loadingState, features);
				}

				return Promise.all(loadingState.readyPromises).then(() => {
					loadingState.loaded = true;
				});
			});
		}

		getLoadedFeatureProfileInfo() {
			let featuresList = [];
			let loadedFeatures = this.getLoadedFeatures();
			let features = Object.keys(loadedFeatures);
			features.forEach( (entry) => {
				let profiles = this.feature2ProfileMap[entry];
				featuresList.push( {
					id: entry,
					profiles: profiles
				} );
			} );
			return featuresList;
		}


		/**
		 * Return the order the features were loaded in
		 * Used for testing to verify the order of loading
		 */
		getFeatureLoadingOrder() {
			return this.loadingOrder;
		}

		_registerModuleInfeatures(module, featureId, features) {
			features[featureId].module = module;
			this.feature2ProfileMap[featureId] = features[featureId].spec.profiles;
		}

		/**
		 *
		 * try to load all the features in one shot, if one fails load all the features one
		 * after the other
		 * use the global require method when trying to load all the features
		 * @param {string[]} classesToLoad - array of class names to load
		 * @param {string[]} featureIds - array of corresponding feature ids
		 * @param {object} features - object containing the feature to process
		 * @returns {promise} - resolve when all the features are loaded
		 */
		_loadFeatureClasses(classesToLoad, featureIds, features) {
			return new Promise((resolve) => {
				let loadingOneByOne = false;
				const that = this;
				require(classesToLoad, function () {
					for (let j = 0; j < arguments.length; j++) {
						that._registerModuleInfeatures(arguments[j], featureIds[j], features);
						FeatureLoader.classes[classesToLoad[j]] = arguments[j];
					}
					resolve(features);
				}, (error) => {
					that.logger.log(error);
					if (!loadingOneByOne) {
						loadingOneByOne = true;
						const promises = [];
						for (let j = 0; j < classesToLoad.length; j++) {
							promises.push(
								ClassFactory.loadModule(classesToLoad[j])
									.then((module) => {
										this._registerModuleInfeatures(module, featureIds[j], features);
										FeatureLoader.classes[classesToLoad[j]] = module;
									}).catch((e) => {
										this._setError(featureIds[j], 'Failed to require feature', e.causedBy, features);
										MODULE_LOADING_ERROR[features[featureIds[j]].spec.class] = e;
									})
							);
						}
						Promise.all(promises).then(() => { resolve(features); });
					}
				});
			});
		}

		_setError(featureName, message, details = '', features = this.features) {
			if (!features[featureName]){
				features[featureName] = {};
			}

			if (details instanceof ErrorEvent) {
				details = {
					message: details.message,
					lineno: details.lineno,
					filename: details.filename
				};
			}

			features[featureName].error = {
				message: message,
				details: details
			};

			// move the failed feature to the main list of features
			this.features[featureName] = features[featureName];

			this.logger.log('Feature loader:' + message, ',loading feature:', featureName, ',details:', details);
		}

		getFeatureErrors() {
			const errors = {};
			let name;
			for (name in this.features) {
				if (this.features[name].error) {
					errors[name] = this.features[name].error;
				}
			}
			return errors;
		}

		_prepareFeature(name, loadingState, features = this.features) {
			// If the feature is not part of the features list that are being currently loaded,
			// then we check the main feature list  (this.features) that contains already loaded features
			// this could happen when a feature that loaded a part of a collection is trying to load one of its dependencies that
			// was previously loaded as part of another colletion.
			if(!features[name] && this.features[name]){
				// If not found in the local feature map, then we try to global that has the loaded features.
				// If we find the feature in the global feature, we will switch to it.
				features = this.features;
			}

			// try #2 - we could be using a feature name with modifier (e.g. state.internal)
			// let us check if we have a modifier in the feature name .
			//this could happen when a feature is attempting to create a dependency and it is using a modifier in the feature name (e.g. state.internal)
			if (!features[name]){
				let resolved = this._resolvePartialFeatureName(name, features);
				if (resolved){
					name = resolved.name;
				}else {
					resolved = this._resolvePartialFeatureName(name, this.features);
					if (resolved){
						name = resolved.name;
						features = this.features;
					}
				}
			}

			if (!features[name]){
				this._setError(name, 'Attempting to load an undefined feature:' + name, '', features);
			}

			if (this._getError(name, features)) {
				features[name].preparePromise = Promise.resolve(name);
			}

			let preparePromise = features[name].preparePromise;
			if (!preparePromise) {
				const requiredFeatures = {};
				const initDepdendents = [];

				// Go over the init dependencies and create them first
				this._getInitDependencies(name, features).forEach((feature) => {
					// if we depend on a feature that is not is part of the feature collection,
					// then check the external feature,

					if (this.isFeature(feature, features) || this.isFeature(feature)){
						requiredFeatures[this._getDependenciesPreferredName(feature, name, features)] = {
							loader: this,
							init: true,
							name: feature
						};
						initDepdendents.push(this._prepareFeature(feature, loadingState, features));
					} else if (this.externalFeatureLoader && this.externalFeatureLoader.isFeature(feature)){
						requiredFeatures[this._getDependenciesPreferredName(feature, name, features)] = {
							loader: this.externalFeatureLoader,
							init: true,
							name: feature
						};
					}
				});

				// Go over the runtime dependencies and create them
				this._getRuntimeDependencies(name, features).forEach((feature) => {
					// if we depend on a feature that is not is part of the feature collection,
					// then check the external feature,

					if (this.isFeature(feature, features) || this.isFeature(feature)){
						requiredFeatures[this._getDependenciesPreferredName(feature, name, features)] = {
							loader: this,
							init: false,
							name: feature
						};
					} else if (this.externalFeatureLoader && this.externalFeatureLoader.isFeature(feature)){
						requiredFeatures[this._getDependenciesPreferredName(feature, name, features)] = {
							loader: this.externalFeatureLoader,
							init: false,
							name: feature
						};
					} else {
						// Could not find the runtime dependency.. let us not consider it an error yet, maybe it is not register
						requiredFeatures[this._getDependenciesPreferredName(feature, name, features)] = {
							loader: this,
							init: false,
							name: feature
						};
					}
				});

				preparePromise = Promise.all(initDepdendents).then(() => {

					// Make sure that all dependencies loaded without an error
					const depErrors = [];
					this._getDependencies(name, features).forEach((featureName) => {
						const error = this._getError(featureName, features);
						if (error) {
							depErrors.push(error);
						}
					});

					// TODO -- improve this loop and the one above
					let canDelayCreation = true;

					// If we already created the init time dependency, then we will not delay the creation of this feature.
					// This is because the delayed creating will happen when either:
					// 1 - the feature is accessed through the getter
					// 2 - one if its init depdendencies was created

					// TODO  -- Possibly at one point, we want to also check external dependencies.
					// This might be needed when we have content feature that registers itself in a dashboard level feature in the initializer.
					this._getInitDependencies(name, features).forEach((featureName) => {
						if (canDelayCreation ){
							canDelayCreation = this._canSkipIsCreatedCheck(featureName) || !(this._isCreated(featureName, features) || this._isCreated(featureName, this.features));
						}
					});

					if (depErrors.length === 0) {
						// All good, create and initialize this feature if needed, otherwise we will lazy create it when requested
						this.loadingOrder.push(name);
						try {
							const params = _.extend({}, this.featureParams);

							const options = this._getOptions(name, features);
							if (options) {
								params.options = options;
							}

							params.features = createFeatureDependencies(name, requiredFeatures, loadingState);
							features[name].params = params;

							if (params.featureSpecs) {
								params.spec = params.featureSpecs[name];
							}


							// Don't delay the create if the feature has an initialize
							// Or if it depends on a feature that was already created -- (in case the feature is not referenced and simply registering itself in one of the dependencies)
							const module = this._getFeatureModule(features[name]);
							if(!canDelayCreation || module.prototype.initialize){
								// we have to create and initialize because this feature needs some async initilize

								const instance = this._createFeature(features[name]);

								return this._initializeFeature(name, features).then(() => {
									// move the feature to the main list of features once initialized
									this.features[name] = features[name];
									if (this.featureNamePrefix){
										this.features[this.featureNamePrefix + '.' + name] = features[name] ;
									}
									this.setParentChildRelation(name, instance);
								});
							} else {

								this.features[name] = features[name];
								if (this.featureNamePrefix){
									this.features[this.featureNamePrefix + '.' + name] = features[name] ;
								}
							}

						} catch (e) {
							this._setError(name, 'Error while creating the feature:' + name, e, features);
						}
					} else {
						this._setError(name, 'Dependency failed to load', depErrors, features);
					}
				});
				features[name].preparePromise = preparePromise;
				// This is uesd by the main feature loading to tell when all features are loaded
				loadingState.readyPromises.push(preparePromise);
			}


			return preparePromise;
		}

		_canSkipIsCreatedCheck(name = '') {
			const allowedFeatures = ['API', 'Logger', 'internal',  'DashboardSettings',
				'Canvas', 'state', 'state.internal', 'Transaction', 'DashboardTheme', 'Palette', 'Models.internal', 'DashboardState',
				'UserProfile', 'DashboardPreferences'];
			return allowedFeatures.includes(name) || allowedFeatures.includes('Dashboard.'+name);
		}

		_initializeFeature(name, features = this.features) {
			const feature = features[name];
			let promise = null;
			try {
				const instance = feature.instance;
				if (typeof (instance.initialize) === 'function') {
					promise = instance.initialize();
				}

				if (promise && promise.then) {
					promise = promise.then(() => {
						this._postFeatureInitialize(feature);
					});
				} else {
					this._postFeatureInitialize(feature);
				}
			} catch (e) {
				this._setError(name, 'Failed to initialize feature:' + name, e, features);
			}

			if (!promise) {
				promise = Promise.resolve();
			}

			return promise.catch (e => {
				this._setError(name, 'Failed to initialize feature: ' + name, e, features);
			});
		}

		_deregisterLifeCycleHandlers(feature) {
			if (feature) {
				if (feature.registeredHandlers) {
					feature.registeredHandlers.forEach(handler => handler.remove());
				}
				feature.registeredHandlers = [];
			}
		}


		// TODO - the lifecycle handlers should not be part of the feature loader
		// The lifeCycle manager is exposed as a feature.. so features can depend on it and register handlers directly with it.
		_postFeatureInitialize(feature) {
			const instance = feature.instance;

			this._deregisterLifeCycleHandlers(feature);

			if (this.lifeCycleManager && typeof (instance.getLifeCycleHandlers) === 'function') {

				var handlers = instance.getLifeCycleHandlers();
				if (!Array.isArray(handlers)) {
					throw new Error('serviceExtension getLifeCycleHandlers method is expected to return an array');
				}
				handlers.forEach((handler) => {
					if (!handler.name || !handler.action) {
						throw new Error('serviceExtension handlers are expected to have a name and action property');
					}
					feature.registeredHandlers.push(this.lifeCycleManager.registerHandler(handler.name, handler.action, handler.priority, handler.triggerIfEventAlreadySent));
				});
			}
		}


		_validateDependencies(features = this.features) {
			// Validate the dependencies for each step
			let name;
			for (name in features) {
				this._validateFeatureDependencies(name, null, features);
			}
		}
		_validateFeatureDependencies(featureId, currentDeps, features = this.features) {
			let dependencies = currentDeps || [];
			if (dependencies.indexOf(featureId) !== -1) {
				throw new Error('The feature with id "' + featureId + '" has a a circular dependency: ' + currentDeps);
			}
			dependencies.push(featureId);
			this._getDependencies(featureId, features).forEach((depItem) => {
				if (!this._getFeatureSpec(depItem, features)) {
					throw new Error('The feature with id "' + featureId + '" has a missing dependency "' + depItem + '"');
				}
				const clonedDepArray = dependencies.concat([]);
				this._validateFeatureDependencies(depItem, clonedDepArray, features);
			});
		}

		_getFeatureSpec(name, features = this.features) {
			return features[name] && features[name].spec || {};
		}


		setFeatureEnabled(name, isEnabled) {
			if (this.features[name]) {
				if (isEnabled) {
					this.features[name].disabled = false;
					this._postFeatureInitialize(this.features[name]);
				} else {
					this.features[name].disabled = true;
					this._deregisterLifeCycleHandlers(this.features[name]);
				}
			}
		}

		destroy() {
			_.each(_.keys(this.features), (featureName) => {

				// TODO -- we should have destroyFeature
				if( this.featureNamePrefix && featureName.startsWith(this.featureNamePrefix + '.')){
					delete this.features[featureName];
				} else {
					if (this.features[featureName].instance){
						this._deregisterLifeCycleHandlers(this.features[featureName]);
						const instance = this._getFeatureInstance(featureName);
						if (instance && instance.destroy) {
							try {
								instance.destroy();
							} catch (e) {
								this._setError(featureName, 'Error while destroying the feature:' + featureName, e);
							}
						}
					}

					delete this.features[featureName];
				}
			});
			for (var prop in this) {
				// clear everything referenced by this object in case someone is still holding on to it.
				if (Object.prototype.hasOwnProperty.call(this, prop)) {
					delete this[prop];
				}
			}
		}

		/**
		 * Get a feature. If a feature is disabled or does not implement the getAPI function, then undefined will be returned.
		 *
		 * If the name is of the form 'myFeature.xyz', We will first try to find a feature that matches the full name,
		 * if we can't find one, then we will look for a feature with name 'myFeature',
		 * and we will pass the value 'xyz' to the getAPI() when we get the API object (i.e. myFeature.getAPI('xyz'))
		 *
		 * @param {String} name
		 */
		getFeature(name) {
			var api;
			let instance = this._getFeatureInstance(name);
			let apiType;
			if (!instance && name){
				const resolved = this._resolvePartialFeatureName(name);
				if (resolved){
					name = resolved.name;
					apiType = resolved.apiType;
					instance = this._getFeatureInstance(name);
				}
			}
			if (this.isFeatureEnabled(name) && !this._getError(name) && instance.getAPI) {
				api = instance.getAPI(apiType);
				//If a public api was requested, only return api's that are marked with the isPublicApi property.
				if (apiType && apiType.split('.').includes('public') && api) {
					api = (api.isPublicApi && apiType==='public') ? api : undefined;
				}
			}
			return api;
		}

		isFeature(name, features = this.features) {
			let exist = features[name];
			if (!exist){
				const resolved = this._resolvePartialFeatureName(name, features);
				if (resolved){
					exist = features[resolved.name];
				}
			}
			return !!exist;
		}

		_isCreated(name, features = this.features) {
			let exist = features[name];
			if (!exist){
				const resolved = this._resolvePartialFeatureName(name, features);
				if (resolved){
					exist = features[resolved.name];
				}
			}
			return exist && exist.instance;
		}


		getLoadedFeatures() {
			// TODO - this is used for serialization .. it will end up creating all features even ones that were not referenced
			// WE can optimize to only use the feature that were created..
			const loadedFeatures = {};
			Object.keys(this.features).forEach(featureName => {
				if (!featureName.startsWith(this.featureNamePrefix + '.')) {
					const featureInstance = this._getFeatureInstance(featureName, { create:false  });
					if (featureInstance) {
						loadedFeatures[featureName] = featureInstance;
					}
				}
			});
			return loadedFeatures;
		}


		_resolvePartialFeatureName(name, featuresMap = this.features){
			// We support the 2 cases:
			//  "a.b.c"  where "a" is the feature and "b.c" is the api type
			// or
			//  "a.b.c"  where "a.b" is the feature and "c" is the api type
			const parts = name.split('.');
			if (parts.length > 1){
				const firstPart = parts[0];
				const lastPart = parts[parts.length - 1];
				const middlePart = parts.splice(1, parts.length - 2).join('.');
				name = middlePart ? firstPart + '.' + middlePart: firstPart;
				if (featuresMap[name]){
					return {
						name: name,
						apiType: lastPart
					};
				} else {
					name = firstPart;
					if (featuresMap[name]){
						return {
							name: name,
							apiType: lastPart? middlePart + '.' + lastPart : middlePart
						};
					}
				}
			}
			return null;
		}

		/**
		 * Indicate if the feature is enabled or not.
		 * @param {boolean} name
		 */
		isFeatureEnabled(name) {
			const instance = this._getFeatureInstance(name, { create:false  });
			return instance && !this.features[name].disabled && (!instance.isEnabled || instance.isEnabled());
		}

		/**
		 * @param tagToMatch - a tag that any feature must include in its getTags() api.
		 * @returns any features that match the tag passed in as an array (empty array is returned if no features match).
		 */
		getMatchingFeatures(tagToMatch) {
			let matchingFeatures = [];
			_.each(_.keys(this.features), (featureName) => {
				if (this.isFeatureEnabled(featureName) && this._getFeatureTags(featureName).indexOf(tagToMatch) >= 0) {
					const featureAPI = this.getFeature(featureName);
					if (featureAPI) {
						matchingFeatures.push(featureAPI);
					}
				}
			});

			return matchingFeatures;
		}

		/**
		 * Enable or disable a feature that matches the supplied tag
		 * @param tagToMatch - a tag that any feature must include in its getTags() api.
		 * @param isEnabled
		 */
		setMatchingFeaturesEnabled(tagToMatch, isEnabled) {
			_.each(_.keys(this.features), (featureName) => {
				const feature = this.features[featureName];
				if (feature && this._getFeatureTags(featureName).indexOf(tagToMatch) >= 0) {
					this.setFeatureEnabled(featureName, isEnabled);
				}
			});
		}
	}

	FeatureLoader.classes = {};

	return FeatureLoader;
});
